import * as React from 'react'
import { GreyEditButton } from 'shared/components/Button/EditButton'
import { Text } from 'shared/components/Text/Text'
import { Handler } from 'shared/helpers/typeHelper'
import { breakpoints } from 'shared/theme/breakpoints'
import styled, { css } from 'styled-components'

import { Card } from '../../../components/Base/Base'
import { containerQuery } from '../../../helpers/utils'
import { darkGray, lightGray1 } from '../../../theme/colors'

interface ProfileCardProps {
  title?: string
  onEdit?: Handler
  children?: React.ReactNode
  className?: string
}

interface ProfileCardItemProps {
  label: React.ReactNode
  children: React.ReactNode
  leftWidth?: string | number
  centerAlign?: boolean
  smallGap?: boolean
}

export function ProfileCard({ onEdit, children, title, className }: ProfileCardProps) {
  return (
    <StyledCard hasSubtleShadow smallPadding align="left" className={className}>
      <Title variant="uppercaseLabel3">{title}</Title>
      <Content>{children}</Content>
      <Edit>{onEdit && <StyledEditButton ariaLabel="edit" onClick={onEdit} />}</Edit>
    </StyledCard>
  )
}

export function ProfileCardItem({ label, children, leftWidth, centerAlign, smallGap }: ProfileCardItemProps) {
  return (
    <StyledProfilePageCardRow $leftWidth={leftWidth} $centerAlign={centerAlign} $smallGap={smallGap}>
      <Text variant="header3" fontColor={lightGray1}>
        {label}
      </Text>
      <Value variant="label2" fontColor={darkGray}>
        {children}
      </Value>
    </StyledProfilePageCardRow>
  )
}

const StyledCard = styled(Card)`
  width: 100%;
  display: grid;
  grid-template-columns: 200px 1fr auto;
  grid-template-areas: 'title content edit';
  gap: 20px;

  @media ${breakpoints.sm} {
    grid-template-columns: 1fr auto;
    grid-template-areas:
      'title title'
      'content edit';
  }
`

const StyledEditButton = styled(GreyEditButton)`
  margin-left: auto;
`

const Title = styled(Text)`
  grid-area: title;
`

const Value = styled(Text)`
  overflow-wrap: break-word;
  word-wrap: break-word;
  word-break: break-all;
  hyphens: auto;

  @media ${breakpoints.sm} {
    word-break: break-word;
  }
`

export const StyledProfilePageCardRow = styled.div<{
  $centerAlign?: boolean
  $leftWidth?: string | number
  $smallGap?: boolean
}>`
  display: grid;
  grid-gap: 20px;
  position: relative;

  ${(p) =>
    css`
      grid-template-columns: ${p.$leftWidth || '130px'} 1fr;
    `}

  ${(p) =>
    p.$smallGap &&
    css`
      grid-gap: 12px;
    `}

  ${(p) =>
    css`
      align-items: ${p.$centerAlign && 'center'};
    `}

  & + & {
    margin-top: 8px;
  }

  ${containerQuery(
    breakpoints.xs,
    css`
      grid-template-columns: 80px 1fr;
    `
  )}
`

const Content = styled.div`
  grid-area: content;
`

const Edit = styled.div`
  grid-area: edit;
`
